import { filterOptions, normalizeOptions } from './picker-options.js';

/**
 * Whether a suggestion represents the currently committed value (shown with a tick).
 * @param {{ type: string, value?: string, name?: string }} s
 * @param {string} value
 * @param {boolean} selectOnly
 * @returns {boolean}
 */
export function isSelectedSuggestion(s, value, selectOnly) {
	if (selectOnly) { return s.type === 'existing' && s.value === value; }
	return s.type === 'committed-custom' || (s.type === 'existing' && s.name === value);
}

/**
 * Viewport-aware placement classes for the suggestion list.
 * @param {boolean} flipUp
 * @param {boolean} alignRight
 * @returns {string}
 */
export function suggestionListClass(flipUp, alignRight) {
	return `picker-suggestions${flipUp ? ' is-flipped' : ''}${alignRight ? ' is-right-aligned' : ''}`;
}

/**
 * Compute the suggestion list for the current query.
 * @param {Object} p
 * @param {boolean} p.selectOnly
 * @param {Array<string | { value: string, label: string }>} p.rawOptions
 * @param {string} p.value
 * @param {string} p.query
 * @param {boolean} p.hasTyped
 * @param {boolean} p.allowCustom
 * @param {boolean} p.clearable
 * @param {string} [p.emptyLabel]
 * @param {(key: string, options?: any) => string} p.t
 * @returns {{ suggestions: any[] }}
 */
export function buildSuggestions(p) {
	if (p.selectOnly) {
		const suggestions = /** @type {any[]} */ ([]);
		if (p.clearable && p.value !== '') {
			suggestions.push({
				type: 'clear', label: p.emptyLabel || p.t('field.select'), value: '',
			});
		}
		for (const opt of filterOptions(normalizeOptions(p.rawOptions), p.query)) {
			suggestions.push({ type: 'existing', label: opt.label, value: opt.value });
		}
		return { suggestions };
	}
	const matches = p.rawOptions.filter((o) => o.toLowerCase().includes(p.query));
	const suggestions = /** @type {any[]} */ (matches.map((name) => ({ type: 'existing', name })));
	const trimmed = p.value.trim();
	if (p.allowCustom && trimmed !== '' && !p.rawOptions.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
		suggestions.push(p.hasTyped ? { type: 'custom', name: trimmed } : { type: 'committed-custom', name: trimmed });
	}
	return { suggestions };
}

/**
 * Resolve what the input shows: empty while the picker is open until the user
 * types; otherwise the typed draft or the committed value.
 * @param {{ open: boolean, hasTyped: boolean, draft: string, selectOnly: boolean, committedLabel: string, value: string }} o
 * @returns {string}
 */
export function displayValue({ open, hasTyped, draft, selectOnly, committedLabel, value }) {
	return open && !hasTyped ? '' : (hasTyped ? draft : (selectOnly ? committedLabel : value));
}
